import { useMemo, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { formatCurrency, exportToCSV, exportToPDF, formatDate, printHtmlDocument } from '@/utils/calculations';
type ReportTab = 'overview' | 'client' | 'item' | 'vendors';

export default function ReportsPage() {
  const { isDark, transfers, sales, clients, items, vendorPayments } = useApp();
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');

  // Client-wise filter state
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientDateFrom, setClientDateFrom] = useState('');
  const [clientDateTo, setClientDateTo] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState<string>('all');
  const [clientSettledFilter, setClientSettledFilter] = useState<string>('all');

  // Item-wise filter state
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [itemDateFrom, setItemDateFrom] = useState('');
  const [itemDateTo, setItemDateTo] = useState('');
  const [itemPaymentFilter, setItemPaymentFilter] = useState<string>('all');
  const [itemCustomerSearch, setItemCustomerSearch] = useState('');

  const allTransfers = transfers;
  const allSales = sales;
  const allClients = clients;
  const allItems = items;

  const transferStats = useMemo(() => ({
    total: allTransfers.reduce((s, t) => s + t.amount, 0),
    myCommission: allTransfers.reduce((s, t) => s + t.myCommission, 0),
    providerCommission: allTransfers.reduce((s, t) => s + t.providerCommission, 0),
    profit: allTransfers.reduce((s, t) => s + t.myProfit, 0),
    completed: allTransfers.filter(t => t.status === 'completed').length,
    pending: allTransfers.filter(t => t.status === 'pending').length,
    cancelled: allTransfers.filter(t => t.status === 'cancelled').length,
    settled: allTransfers.filter(t => t.isSettled).length,
  }), [allTransfers]);

  const salesStats = useMemo(() => ({
    revenue: allSales.reduce((s, x) => s + x.totalSaleValue, 0),
    cogs: allSales.reduce((s, x) => s + x.cogs, 0),
    gross: allSales.reduce((s, x) => s + x.grossProfit, 0),
    net: allSales.reduce((s, x) => s + x.netProfit, 0),
    paid: allSales.filter(s => s.paymentStatus === 'paid').length,
    pending: allSales.filter(s => s.paymentStatus === 'pending').length,
  }), [allSales]);

  const topClients = useMemo(() => [...allClients].sort((a, b) => b.totalTransferred - a.totalTransferred).slice(0, 5), [allClients]);
  const topItems = useMemo(() => {
    const map: Record<string, { name: string; qty: number; revenue: number }> = {};
    allSales.forEach(s => {
      if (!map[s.itemId]) map[s.itemId] = { name: s.itemName, qty: 0, revenue: 0 };
      map[s.itemId].qty += s.quantitySold;
      map[s.itemId].revenue += s.totalSaleValue;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [allSales]);

  const inventoryValue = useMemo(() => allItems.reduce((s, i) => s + i.purchasePrice * i.currentStock, 0), [allItems]);
  const lowStock = allItems.filter(i => i.currentStock <= i.lowStockAlert);

  // Vendor payment stats
  const totalOwed = useMemo(() => allItems.reduce((s, i) => s + i.totalPurchaseValue, 0), [allItems]);
  const totalPaid = useMemo(() => vendorPayments.reduce((s, p) => s + p.amount, 0), [vendorPayments]);

  const vendorBreakdown = useMemo(() => {
    const map: Record<string, { vendorName: string; totalPurchased: number; totalPaid: number; paymentCount: number; lastPayment: string }> = {};
    allItems.forEach(i => {
      const key = i.supplier || 'Unknown Vendor';
      if (!map[key]) map[key] = { vendorName: key, totalPurchased: 0, totalPaid: 0, paymentCount: 0, lastPayment: '' };
      map[key].totalPurchased += i.totalPurchaseValue;
    });
    vendorPayments.forEach(p => {
      const key = p.vendorName;
      if (!map[key]) map[key] = { vendorName: key, totalPurchased: 0, totalPaid: 0, paymentCount: 0, lastPayment: '' };
      map[key].totalPaid += p.amount;
      map[key].paymentCount += 1;
      if (!map[key].lastPayment || p.date > map[key].lastPayment) map[key].lastPayment = p.date;
    });
    return Object.values(map).sort((a, b) => (b.totalPurchased - b.totalPaid) - (a.totalPurchased - a.totalPaid));
  }, [allItems, vendorPayments]);

  const paymentTrend = useMemo(() => {
    const months: { label: string; key: string; amount: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });
      const amount = vendorPayments.filter(p => p.date.startsWith(key)).reduce((s, p) => s + p.amount, 0);
      months.push({ label, key, amount });
    }
    return months;
  }, [vendorPayments]);

  const maxTrendAmount = Math.max(...paymentTrend.map(m => m.amount), 1);

  // ── CLIENT-WISE REPORT ──
  const selectedClient = useMemo(() => allClients.find(c => c.id === selectedClientId) || null, [allClients, selectedClientId]);

  const clientTransfers = useMemo(() => {
    if (!selectedClientId) return [];
    return allTransfers.filter(t => {
      if (t.clientId !== selectedClientId) return false;
      if (clientDateFrom && t.date < clientDateFrom) return false;
      if (clientDateTo && t.date > clientDateTo) return false;
      if (clientStatusFilter !== 'all' && t.status !== clientStatusFilter) return false;
      if (clientSettledFilter === 'settled' && !t.isSettled) return false;
      if (clientSettledFilter === 'unsettled' && t.isSettled) return false;
      return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allTransfers, selectedClientId, clientDateFrom, clientDateTo, clientStatusFilter, clientSettledFilter]);

  const clientStats = useMemo(() => ({
    totalAmount: clientTransfers.reduce((s, t) => s + t.amount, 0),
    myCommission: clientTransfers.reduce((s, t) => s + t.myCommission, 0),
    providerCommission: clientTransfers.reduce((s, t) => s + t.providerCommission, 0),
    profit: clientTransfers.reduce((s, t) => s + t.myProfit, 0),
    settled: clientTransfers.filter(t => t.isSettled).length,
    unsettled: clientTransfers.filter(t => !t.isSettled).length,
    completed: clientTransfers.filter(t => t.status === 'completed').length,
    pending: clientTransfers.filter(t => t.status === 'pending').length,
    netDue: clientTransfers.reduce((s, t) => s + t.netAmountDue, 0),
  }), [clientTransfers]);

  // ── ITEM-WISE REPORT ──
  const selectedItem = useMemo(() => allItems.find(i => i.id === selectedItemId) || null, [allItems, selectedItemId]);

  const itemSales = useMemo(() => {
    if (!selectedItemId) return [];
    return allSales.filter(s => {
      if (s.itemId !== selectedItemId) return false;
      if (itemDateFrom && s.date < itemDateFrom) return false;
      if (itemDateTo && s.date > itemDateTo) return false;
      if (itemPaymentFilter !== 'all' && s.paymentStatus !== itemPaymentFilter) return false;
      if (itemCustomerSearch && !s.customerName.toLowerCase().includes(itemCustomerSearch.toLowerCase())) return false;
      return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allSales, selectedItemId, itemDateFrom, itemDateTo, itemPaymentFilter, itemCustomerSearch]);

  const itemStats = useMemo(() => ({
    totalQty: itemSales.reduce((s, x) => s + x.quantitySold, 0),
    totalRevenue: itemSales.reduce((s, x) => s + x.totalSaleValue, 0),
    totalCogs: itemSales.reduce((s, x) => s + x.cogs, 0),
    totalGross: itemSales.reduce((s, x) => s + x.grossProfit, 0),
    totalNet: itemSales.reduce((s, x) => s + x.netProfit, 0),
    paid: itemSales.filter(s => s.paymentStatus === 'paid').length,
    pending: itemSales.filter(s => s.paymentStatus === 'pending').length,
    avgSellingPrice: itemSales.length > 0 ? itemSales.reduce((s, x) => s + x.sellingPrice, 0) / itemSales.length : 0,
  }), [itemSales]);

  // Styles
  const card = `rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`;
  const label = `text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const val = `text-xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`;
  const th = `text-xs font-semibold uppercase tracking-wide px-4 py-3 text-left whitespace-nowrap ${isDark ? 'text-gray-400 bg-gray-800' : 'text-gray-500 bg-gray-50'}`;
  const td = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-emerald-500`;
  const selectCls = `px-3 py-2 rounded-lg border text-sm cursor-pointer ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-emerald-500`;

  const tabs: { key: ReportTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Business Overview', icon: 'ri-dashboard-line' },
    { key: 'client', label: 'Client Report', icon: 'ri-user-line' },
    { key: 'item', label: 'Item Report', icon: 'ri-box-3-line' },
    { key: 'vendors', label: 'Vendor Payables', icon: 'ri-store-2-line' },
  ];

  const handleExportTransfers = () => exportToCSV(allTransfers.map(t => ({ Date: t.date, Client: t.clientName, TxnID: t.transactionId, Amount: t.amount, MyCommission: t.myCommission, ProviderCommission: t.providerCommission, Profit: t.myProfit, Status: t.status, Settled: t.isSettled ? 'Yes' : 'No' })), 'transfers-report');
  const handleExportSales = () => exportToCSV(allSales.map(s => ({ Date: s.date, Customer: s.customerName, Item: s.itemName, Qty: s.quantitySold, Price: s.sellingPrice, Total: s.totalSaleValue, COGS: s.cogs, GrossProfit: s.grossProfit, NetProfit: s.netProfit, Status: s.paymentStatus })), 'sales-report');
  const handleExportVendorCSV = () => exportToCSV(vendorBreakdown.map(v => ({ Vendor: v.vendorName, TotalPurchased: v.totalPurchased, TotalPaid: v.totalPaid, BalanceOwed: v.totalPurchased - v.totalPaid, Payments: v.paymentCount, LastPayment: v.lastPayment || 'N/A' })), 'vendor-payables-report');

  const handleExportPDF = () => exportToPDF('Business Summary Report', ['Metric', 'Value'], [
    ['Total Transferred', formatCurrency(transferStats.total)],
    ['My Commission', formatCurrency(transferStats.myCommission)],
    ['Provider Commission', formatCurrency(transferStats.providerCommission)],
    ['Transfer Profit', formatCurrency(transferStats.profit)],
    ['Total Sales Revenue', formatCurrency(salesStats.revenue)],
    ['COGS', formatCurrency(salesStats.cogs)],
    ['Gross Profit', formatCurrency(salesStats.gross)],
    ['Net Profit', formatCurrency(salesStats.net)],
    ['Inventory Value', formatCurrency(inventoryValue)],
    ['Total Owed to Vendors', formatCurrency(totalOwed)],
    ['Total Paid to Vendors', formatCurrency(totalPaid)],
    ['Vendor Balance Remaining', formatCurrency(totalOwed - totalPaid)],
  ]);

  const handleExportClientPDF = () => {
    if (!selectedClient) return;
    const rows = clientTransfers.map(t => `
      <tr>
        <td>${formatDate(t.date)}</td>
        <td>${t.transactionId}</td>
        <td>${t.type === 'client_paid_me' ? 'Client Paid Me' : 'I Paid Client'}</td>
        <td>${formatCurrency(t.amount)}</td>
        <td>${formatCurrency(t.myCommission)}</td>
        <td>${formatCurrency(t.providerCommission)}</td>
        <td style="color:#10b981;font-weight:600">${formatCurrency(t.myProfit)}</td>
        <td>${formatCurrency(t.netAmountDue)}</td>
        <td><span style="color:${t.status === 'completed' ? '#10b981' : t.status === 'pending' ? '#f59e0b' : '#ef4444'}">${t.status}</span></td>
        <td>${t.isSettled ? 'Yes' : 'No'}</td>
        <td>${t.notes || '—'}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Client Report - ${selectedClient.name}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:32px;color:#111}
      h1{font-size:22px;margin-bottom:4px}
      .sub{color:#666;font-size:13px;margin-bottom:24px}
      .summary{display:flex;gap:16px;margin-bottom:28px;flex-wrap:wrap}
      .sc{background:#f9f9f9;border:1px solid #eee;border-radius:8px;padding:12px 18px;min-width:120px}
      .sc .lbl{font-size:11px;color:#888;text-transform:uppercase;letter-spacing:.05em}
      .sc .v{font-size:18px;font-weight:700;margin-top:4px}
      .green{color:#10b981}.red{color:#ef4444}.amber{color:#f59e0b}
      h2{font-size:15px;margin:24px 0 10px;border-bottom:1px solid #eee;padding-bottom:6px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px}
      th{background:#f3f4f6;text-align:left;padding:8px 10px;font-size:11px;text-transform:uppercase;color:#555}
      td{padding:8px 10px;font-size:12px;border-bottom:1px solid #f0f0f0}
      .footer{margin-top:32px;font-size:12px;color:#aaa;border-top:1px solid #eee;padding-top:12px}
    </style></head><body>
    <h1>Client Statement: ${selectedClient.name}</h1>
    <div class="sub">Generated: ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })} &nbsp;·&nbsp; ${clientTransfers.length} transactions</div>
    <div class="summary">
      <div class="sc"><div class="lbl">Total Transferred</div><div class="v">${formatCurrency(clientStats.totalAmount)}</div></div>
      <div class="sc"><div class="lbl">My Commission</div><div class="v">${formatCurrency(clientStats.myCommission)}</div></div>
      <div class="sc"><div class="lbl">Provider Commission</div><div class="v">${formatCurrency(clientStats.providerCommission)}</div></div>
      <div class="sc"><div class="lbl">My Profit</div><div class="v green">${formatCurrency(clientStats.profit)}</div></div>
      <div class="sc"><div class="lbl">Net Due</div><div class="v">${formatCurrency(clientStats.netDue)}</div></div>
      <div class="sc"><div class="lbl">Settled</div><div class="v green">${clientStats.settled}</div></div>
      <div class="sc"><div class="lbl">Unsettled</div><div class="v amber">${clientStats.unsettled}</div></div>
    </div>
    <h2>Transaction History</h2>
    <table><thead><tr><th>Date</th><th>Txn ID</th><th>Type</th><th>Amount</th><th>My Comm.</th><th>Provider Comm.</th><th>Profit</th><th>Net Due</th><th>Status</th><th>Settled</th><th>Notes</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="11" style="text-align:center;color:#aaa">No transactions</td></tr>'}</tbody></table>
    <div class="footer">BizFlow Business Dashboard &nbsp;·&nbsp; Client statement for ${selectedClient.name}</div>
    </body></html>`;
    printHtmlDocument(html, `client-report-${selectedClient.name}`);
  };

  const handleExportClientCSV = () => {
    if (!selectedClient) return;
    exportToCSV(clientTransfers.map(t => ({
      Date: t.date, TxnID: t.transactionId, Type: t.type, Amount: t.amount,
      MyCommission: t.myCommission, ProviderCommission: t.providerCommission,
      Profit: t.myProfit, NetDue: t.netAmountDue, Status: t.status,
      Settled: t.isSettled ? 'Yes' : 'No', Notes: t.notes,
    })), `client-report-${selectedClient.name.replace(/\s+/g, '-')}`);
  };

  const handleExportItemPDF = () => {
    if (!selectedItem) return;
    const rows = itemSales.map(s => `
      <tr>
        <td>${formatDate(s.date)}</td>
        <td>${s.customerName}</td>
        <td>${s.quantitySold}</td>
        <td>${formatCurrency(s.sellingPrice)}</td>
        <td>${s.discount > 0 ? formatCurrency(s.discount) : '—'}</td>
        <td>${formatCurrency(s.totalSaleValue)}</td>
        <td>${formatCurrency(s.cogs)}</td>
        <td style="color:#10b981;font-weight:600">${formatCurrency(s.grossProfit)}</td>
        <td style="color:#10b981;font-weight:600">${formatCurrency(s.netProfit)}</td>
        <td><span style="color:${s.paymentStatus === 'paid' ? '#10b981' : s.paymentStatus === 'pending' ? '#f59e0b' : '#3b82f6'}">${s.paymentStatus}</span></td>
        <td>${s.notes || '—'}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Item Report - ${selectedItem.name}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:32px;color:#111}
      h1{font-size:22px;margin-bottom:4px}
      .sub{color:#666;font-size:13px;margin-bottom:24px}
      .summary{display:flex;gap:16px;margin-bottom:28px;flex-wrap:wrap}
      .sc{background:#f9f9f9;border:1px solid #eee;border-radius:8px;padding:12px 18px;min-width:120px}
      .sc .lbl{font-size:11px;color:#888;text-transform:uppercase;letter-spacing:.05em}
      .sc .v{font-size:18px;font-weight:700;margin-top:4px}
      .green{color:#10b981}.red{color:#ef4444}
      .info{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:14px 18px;margin-bottom:20px;display:flex;gap:24px;flex-wrap:wrap}
      .info span{font-size:12px;color:#555}
      .info strong{font-size:13px;color:#111}
      h2{font-size:15px;margin:24px 0 10px;border-bottom:1px solid #eee;padding-bottom:6px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px}
      th{background:#f3f4f6;text-align:left;padding:8px 10px;font-size:11px;text-transform:uppercase;color:#555}
      td{padding:8px 10px;font-size:12px;border-bottom:1px solid #f0f0f0}
      .footer{margin-top:32px;font-size:12px;color:#aaa;border-top:1px solid #eee;padding-top:12px}
    </style></head><body>
    <h1>Item Sales Report: ${selectedItem.name}</h1>
    <div class="sub">Generated: ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })} &nbsp;·&nbsp; ${itemSales.length} sales</div>
    <div class="info">
      <div><span>SKU</span><br/><strong>${selectedItem.sku}</strong></div>
      <div><span>Category</span><br/><strong>${selectedItem.category}</strong></div>
      <div><span>Purchase Price</span><br/><strong>${formatCurrency(selectedItem.purchasePrice)}</strong></div>
      <div><span>Current Stock</span><br/><strong>${selectedItem.currentStock} units</strong></div>
      <div><span>Supplier</span><br/><strong>${selectedItem.supplier || '—'}</strong></div>
    </div>
    <div class="summary">
      <div class="sc"><div class="lbl">Units Sold</div><div class="v">${itemStats.totalQty}</div></div>
      <div class="sc"><div class="lbl">Revenue</div><div class="v">${formatCurrency(itemStats.totalRevenue)}</div></div>
      <div class="sc"><div class="lbl">COGS</div><div class="v">${formatCurrency(itemStats.totalCogs)}</div></div>
      <div class="sc"><div class="lbl">Gross Profit</div><div class="v green">${formatCurrency(itemStats.totalGross)}</div></div>
      <div class="sc"><div class="lbl">Net Profit</div><div class="v green">${formatCurrency(itemStats.totalNet)}</div></div>
      <div class="sc"><div class="lbl">Avg Sell Price</div><div class="v">${formatCurrency(itemStats.avgSellingPrice)}</div></div>
    </div>
    <h2>Sales History</h2>
    <table><thead><tr><th>Date</th><th>Customer</th><th>Qty</th><th>Sell Price</th><th>Discount</th><th>Total</th><th>COGS</th><th>Gross Profit</th><th>Net Profit</th><th>Payment</th><th>Notes</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="11" style="text-align:center;color:#aaa">No sales</td></tr>'}</tbody></table>
    <div class="footer">BizFlow Business Dashboard &nbsp;·&nbsp; Item report for ${selectedItem.name}</div>
    </body></html>`;
    printHtmlDocument(html, `item-report-${selectedItem.name}`);
  };

  const handleExportItemCSV = () => {
    if (!selectedItem) return;
    exportToCSV(itemSales.map(s => ({
      Date: s.date, Customer: s.customerName, Qty: s.quantitySold,
      SellPrice: s.sellingPrice, Discount: s.discount, Total: s.totalSaleValue,
      COGS: s.cogs, GrossProfit: s.grossProfit, NetProfit: s.netProfit,
      PaymentStatus: s.paymentStatus, Notes: s.notes,
    })), `item-report-${selectedItem.name.replace(/\s+/g, '-')}`);
  };

  const handleExportVendorPDF = () => {
    const rows = vendorBreakdown.map(v => {
      const balance = v.totalPurchased - v.totalPaid;
      const pct = v.totalPurchased > 0 ? Math.round((v.totalPaid / v.totalPurchased) * 100) : 100;
      return `<tr>
        <td>${v.vendorName}</td>
        <td>${formatCurrency(v.totalPurchased)}</td>
        <td style="color:#10b981">${formatCurrency(v.totalPaid)}</td>
        <td style="color:${balance > 0 ? '#ef4444' : '#10b981'};font-weight:700">${formatCurrency(balance)}</td>
        <td>${v.paymentCount}</td>
        <td>${v.lastPayment ? formatDate(v.lastPayment) : '—'}</td>
        <td>${pct}%</td>
      </tr>`;
    }).join('');
    const paymentRows = [...vendorPayments].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(p =>
      `<tr><td>${formatDate(p.date)}</td><td>${p.vendorName}</td><td>${p.itemName || '—'}</td><td style="color:#10b981;font-weight:600">${formatCurrency(p.amount)}</td><td>${p.referenceNote || '—'}</td></tr>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><title>Vendor Payables Report</title>
    <style>
      body{font-family:Arial,sans-serif;padding:32px;color:#111}
      h1{font-size:22px;margin-bottom:4px}
      .sub{color:#666;font-size:13px;margin-bottom:24px}
      .summary{display:flex;gap:20px;margin-bottom:28px;flex-wrap:wrap}
      .sc{background:#f9f9f9;border:1px solid #eee;border-radius:8px;padding:14px 20px;min-width:130px}
      .sc .lbl{font-size:11px;color:#888;text-transform:uppercase;letter-spacing:.05em}
      .sc .v{font-size:20px;font-weight:700;margin-top:4px}
      .red{color:#ef4444}.green{color:#10b981}
      h2{font-size:15px;margin:24px 0 10px;border-bottom:1px solid #eee;padding-bottom:6px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px}
      th{background:#f3f4f6;text-align:left;padding:8px 10px;font-size:11px;text-transform:uppercase;color:#555}
      td{padding:8px 10px;font-size:13px;border-bottom:1px solid #f0f0f0}
      .footer{margin-top:32px;font-size:12px;color:#aaa;border-top:1px solid #eee;padding-top:12px}
    </style></head><body>
    <h1>Vendor Payables Report</h1>
    <div class="sub">Generated: ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
    <div class="summary">
      <div class="sc"><div class="lbl">Total Owed</div><div class="v">${formatCurrency(totalOwed)}</div></div>
      <div class="sc"><div class="lbl">Total Paid</div><div class="v green">${formatCurrency(totalPaid)}</div></div>
      <div class="sc"><div class="lbl">Balance Remaining</div><div class="v ${(totalOwed - totalPaid) > 0 ? 'red' : 'green'}">${formatCurrency(totalOwed - totalPaid)}</div></div>
      <div class="sc"><div class="lbl">Vendors</div><div class="v">${vendorBreakdown.length}</div></div>
      <div class="sc"><div class="lbl">Payments Made</div><div class="v">${vendorPayments.length}</div></div>
    </div>
    <h2>Vendor Summary</h2>
    <table><thead><tr><th>Vendor</th><th>Total Purchased</th><th>Total Paid</th><th>Balance Owed</th><th>Payments</th><th>Last Payment</th><th>% Paid</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="7" style="text-align:center;color:#aaa">No vendor data</td></tr>'}</tbody></table>
    <h2>All Payment Transactions</h2>
    <table><thead><tr><th>Date</th><th>Vendor</th><th>Item</th><th>Amount</th><th>Reference</th></tr></thead>
    <tbody>${paymentRows || '<tr><td colspan="5" style="text-align:center;color:#aaa">No payments recorded</td></tr>'}</tbody></table>
    <div class="footer">BizFlow Business Dashboard &nbsp;·&nbsp; Auto-generated vendor payables report.</div>
    </body></html>`;
    printHtmlDocument(html, 'vendor-payables-report');
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      completed: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      cancelled: 'bg-red-100 text-red-700',
      paid: 'bg-emerald-100 text-emerald-700',
      partial: 'bg-teal-100 text-teal-700',
    };
    return `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`;
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Reports</h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Business performance overview &amp; detailed analysis</p>
        </div>
        <div className="sm:ml-auto flex gap-2 flex-wrap">
          <button onClick={handleExportTransfers} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-file-excel-line" /> Transfers CSV</button>
          <button onClick={handleExportSales} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-file-excel-line" /> Sales CSV</button>
          <button onClick={handleExportVendorCSV} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-file-excel-line" /> Vendors CSV</button>
          <button onClick={handleExportPDF} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> Full PDF</button>
        </div>
      </div>

      {/* Report Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl w-fit flex-wrap ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${activeTab === tab.key ? 'bg-emerald-500 text-white shadow-sm' : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <>
          <div className={card}>
            <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Money Transfer Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><p className={label}>Total Transferred</p><p className={`${val} text-emerald-500`}>{formatCurrency(transferStats.total)}</p></div>
              <div><p className={label}>My Commission</p><p className={val}>{formatCurrency(transferStats.myCommission)}</p></div>
              <div><p className={label}>Provider Commission</p><p className={val}>{formatCurrency(transferStats.providerCommission)}</p></div>
              <div><p className={label}>My Profit</p><p className={`${val} text-emerald-500`}>{formatCurrency(transferStats.profit)}</p></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
              {[{ label: 'Completed', value: transferStats.completed, color: 'text-emerald-500' }, { label: 'Pending', value: transferStats.pending, color: 'text-amber-500' }, { label: 'Cancelled', value: transferStats.cancelled, color: 'text-red-500' }, { label: 'Settled', value: transferStats.settled, color: 'text-teal-500' }].map(s => (
                <div key={s.label}><p className={label}>{s.label}</p><p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p></div>
              ))}
            </div>
          </div>

          <div className={card}>
            <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Sales Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><p className={label}>Revenue</p><p className={`${val} text-emerald-500`}>{formatCurrency(salesStats.revenue)}</p></div>
              <div><p className={label}>COGS</p><p className={val}>{formatCurrency(salesStats.cogs)}</p></div>
              <div><p className={label}>Gross Profit</p><p className={val}>{formatCurrency(salesStats.gross)}</p></div>
              <div><p className={label}>Net Profit</p><p className={`${val} text-emerald-500`}>{formatCurrency(salesStats.net)}</p></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
              <div><p className={label}>Paid Sales</p><p className="text-2xl font-bold mt-1 text-emerald-500">{salesStats.paid}</p></div>
              <div><p className={label}>Pending Sales</p><p className="text-2xl font-bold mt-1 text-amber-500">{salesStats.pending}</p></div>
              <div><p className={label}>Inventory Value</p><p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(inventoryValue)}</p></div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className={card}>
              <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Top Clients by Volume</h3>
              <div className="space-y-3">
                {topClients.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between mb-1"><p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{c.name}</p><p className={`text-sm font-semibold ml-2 whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(c.totalTransferred)}</p></div>
                      <div className={`h-1.5 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}><div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${Math.min(100, (c.totalTransferred / (topClients[0]?.totalTransferred || 1)) * 100)}%` }} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={card}>
              <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Top Selling Items</h3>
              <div className="space-y-3">
                {topItems.map((item, i) => (
                  <div key={item.name} className="flex items-center gap-3">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between mb-1"><p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{item.name}</p><p className={`text-sm font-semibold ml-2 whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(item.revenue)}</p></div>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.qty} units sold</p>
                    </div>
                  </div>
                ))}
                {topItems.length === 0 && <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No sales data yet</p>}
              </div>
            </div>
          </div>

          {lowStock.length > 0 && (
            <div className={card}>
              <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Low Stock Items</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {lowStock.map(i => (
                  <div key={i.id} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-red-50'}`}>
                    <div><p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{i.name}</p><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{i.sku}</p></div>
                    <div className="text-right"><p className="text-sm font-bold text-red-500">{i.currentStock} left</p><p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Alert: {i.lowStockAlert}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── CLIENT REPORT TAB ── */}
      {activeTab === 'client' && (
        <>
          {/* Filters */}
          <div className={`${card} !p-4`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Filter by Client</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="lg:col-span-2">
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select Client</label>
                <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} className={`${selectCls} w-full`}>
                  <option value="">— Choose a client —</option>
                  {allClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date From</label>
                <input type="date" value={clientDateFrom} onChange={e => setClientDateFrom(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date To</label>
                <input type="date" value={clientDateTo} onChange={e => setClientDateTo(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</label>
                <select value={clientStatusFilter} onChange={e => setClientStatusFilter(e.target.value)} className={`${selectCls} w-full`}>
                  <option value="all">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ background: isDark ? '#1f2937' : '#f3f4f6' }}>
                {[{ v: 'all', l: 'All' }, { v: 'settled', l: 'Settled' }, { v: 'unsettled', l: 'Unsettled' }].map(opt => (
                  <button key={opt.v} onClick={() => setClientSettledFilter(opt.v)}
                    className={`px-3 py-1 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${clientSettledFilter === opt.v ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
                    {opt.l}
                  </button>
                ))}
              </div>
              {selectedClientId && (
                <div className="sm:ml-auto flex gap-2">
                  <button onClick={handleExportClientCSV} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-file-excel-line" /> Export CSV</button>
                  <button onClick={handleExportClientPDF} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> Export PDF</button>
                </div>
              )}
            </div>
          </div>

          {!selectedClientId ? (
            <div className={`${card} flex flex-col items-center justify-center py-16`}>
              <div className={`w-16 h-16 flex items-center justify-center rounded-full mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <i className={`ri-user-search-line text-2xl ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
              <p className={`text-base font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select a client to view their report</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Choose from the dropdown above to see full transaction history and stats</p>
            </div>
          ) : (
            <>
              {/* Client Profile Card */}
              {selectedClient && (
                <div className={`${card} !p-0 overflow-hidden`}>
                  <div className={`px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4 border-b ${isDark ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full text-lg font-bold flex-shrink-0 bg-emerald-100 text-emerald-700`}>
                      {selectedClient.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedClient.name}</h3>
                      <div className="flex flex-wrap gap-3 mt-1">
                        {selectedClient.phone && <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-phone-line" />{selectedClient.phone}</span>}
                        {selectedClient.email && <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-mail-line" />{selectedClient.email}</span>}
                        <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-calendar-line" />Client since {formatDate(selectedClient.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex gap-4 flex-wrap">
                      <div className="text-right">
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Current Balance</p>
                        <p className={`text-lg font-bold ${selectedClient.balance > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>{formatCurrency(selectedClient.balance)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Transferred</p>
                        <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(selectedClient.totalTransferred)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-y sm:divide-y-0" style={{ borderColor: isDark ? '#1f2937' : '#f3f4f6' }}>
                    {[
                      { label: 'Filtered Transfers', value: clientTransfers.length.toString(), color: '' },
                      { label: 'Total Amount', value: formatCurrency(clientStats.totalAmount), color: '' },
                      { label: 'My Commission', value: formatCurrency(clientStats.myCommission), color: '' },
                      { label: 'Provider Comm.', value: formatCurrency(clientStats.providerCommission), color: '' },
                      { label: 'My Profit', value: formatCurrency(clientStats.profit), color: 'text-emerald-500' },
                      { label: 'Settled', value: clientStats.settled.toString(), color: 'text-emerald-500' },
                      { label: 'Unsettled', value: clientStats.unsettled.toString(), color: 'text-amber-500' },
                    ].map(s => (
                      <div key={s.label} className="px-4 py-3">
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
                        <p className={`text-sm font-bold mt-0.5 ${s.color || (isDark ? 'text-white' : 'text-gray-900')}`}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transactions Table */}
              <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Transaction History
                    <span className={`ml-2 text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({clientTransfers.length} records)</span>
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className={th}>Date</th>
                        <th className={th}>Txn ID</th>
                        <th className={th}>Type</th>
                        <th className={th}>Amount</th>
                        <th className={th}>My Comm.</th>
                        <th className={th}>Provider Comm.</th>
                        <th className={th}>My Profit</th>
                        <th className={th}>Net Due</th>
                        <th className={th}>Status</th>
                        <th className={th}>Settled</th>
                        <th className={th}>Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {clientTransfers.map(t => (
                        <tr key={t.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                          <td className={td}>{formatDate(t.date)}</td>
                          <td className={`${td} font-mono text-xs`}>{t.transactionId}</td>
                          <td className={td}>
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${t.type === 'client_paid_me' ? 'bg-teal-100 text-teal-700' : 'bg-orange-100 text-orange-700'}`}>
                              <i className={t.type === 'client_paid_me' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} />
                              {t.type === 'client_paid_me' ? 'Client Paid Me' : 'I Paid Client'}
                            </span>
                          </td>
                          <td className={`${td} font-semibold`}>{formatCurrency(t.amount)}</td>
                          <td className={td}>{formatCurrency(t.myCommission)}</td>
                          <td className={td}>{formatCurrency(t.providerCommission)}</td>
                          <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(t.myProfit)}</td>
                          <td className={`${td} font-semibold`}>{formatCurrency(t.netAmountDue)}</td>
                          <td className={td}><span className={statusBadge(t.status)}>{t.status}</span></td>
                          <td className={td}>
                            <span className={`inline-flex items-center gap-1 text-xs font-medium ${t.isSettled ? 'text-emerald-500' : 'text-amber-500'}`}>
                              <i className={t.isSettled ? 'ri-checkbox-circle-line' : 'ri-time-line'} />
                              {t.isSettled ? 'Settled' : 'Pending'}
                            </span>
                          </td>
                          <td className={`${td} max-w-xs truncate`}>{t.notes || '—'}</td>
                        </tr>
                      ))}
                      {clientTransfers.length === 0 && (
                        <tr><td colSpan={11} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No transactions match the selected filters</td></tr>
                      )}
                    </tbody>
                    {clientTransfers.length > 0 && (
                      <tfoot className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                        <tr>
                          <td colSpan={3} className={`px-4 py-3 text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Totals ({clientTransfers.length})</td>
                          <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(clientStats.totalAmount)}</td>
                          <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(clientStats.myCommission)}</td>
                          <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(clientStats.providerCommission)}</td>
                          <td className="px-4 py-3 text-sm font-bold text-emerald-500">{formatCurrency(clientStats.profit)}</td>
                          <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(clientStats.netDue)}</td>
                          <td colSpan={3} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── ITEM REPORT TAB ── */}
      {activeTab === 'item' && (
        <>
          {/* Filters */}
          <div className={`${card} !p-4`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Filter by Item</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="lg:col-span-2">
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select Item</label>
                <select value={selectedItemId} onChange={e => setSelectedItemId(e.target.value)} className={`${selectCls} w-full`}>
                  <option value="">— Choose an item —</option>
                  {allItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.sku})</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date From</label>
                <input type="date" value={itemDateFrom} onChange={e => setItemDateFrom(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date To</label>
                <input type="date" value={itemDateTo} onChange={e => setItemDateTo(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payment Status</label>
                <select value={itemPaymentFilter} onChange={e => setItemPaymentFilter(e.target.value)} className={`${selectCls} w-full`}>
                  <option value="all">All Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <div className="relative flex-1 max-w-xs">
                <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search by customer name..."
                  value={itemCustomerSearch}
                  onChange={e => setItemCustomerSearch(e.target.value)}
                  className={`${inputCls} pl-8`}
                />
              </div>
              {selectedItemId && (
                <div className="sm:ml-auto flex gap-2">
                  <button onClick={handleExportItemCSV} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-file-excel-line" /> Export CSV</button>
                  <button onClick={handleExportItemPDF} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> Export PDF</button>
                </div>
              )}
            </div>
          </div>

          {!selectedItemId ? (
            <div className={`${card} flex flex-col items-center justify-center py-16`}>
              <div className={`w-16 h-16 flex items-center justify-center rounded-full mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <i className={`ri-box-3-line text-2xl ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
              <p className={`text-base font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select an item to view its report</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Choose from the dropdown above to see full sales history and profitability</p>
            </div>
          ) : (
            <>
              {/* Item Info Card */}
              {selectedItem && (
                <div className={`${card} !p-0 overflow-hidden`}>
                  <div className={`px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4 border-b ${isDark ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
                    <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 bg-teal-100 text-teal-700`}>
                      <i className="ri-box-3-line text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedItem.name}</h3>
                      <div className="flex flex-wrap gap-3 mt-1">
                        <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-barcode-line" />SKU: {selectedItem.sku}</span>
                        <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-price-tag-3-line" />{selectedItem.category}</span>
                        <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-store-2-line" />{selectedItem.supplier || 'No supplier'}</span>
                        <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><i className="ri-money-dollar-circle-line" />Purchase: {formatCurrency(selectedItem.purchasePrice)}/unit</span>
                      </div>
                    </div>
                    <div className="flex gap-4 flex-wrap">
                      <div className="text-right">
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Current Stock</p>
                        <p className={`text-lg font-bold ${selectedItem.currentStock <= selectedItem.lowStockAlert ? 'text-red-500' : isDark ? 'text-white' : 'text-gray-900'}`}>{selectedItem.currentStock} units</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Purchase Value</p>
                        <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(selectedItem.totalPurchaseValue)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y sm:divide-y-0" style={{ borderColor: isDark ? '#1f2937' : '#f3f4f6' }}>
                    {[
                      { label: 'Units Sold', value: itemStats.totalQty.toString(), color: '' },
                      { label: 'Total Revenue', value: formatCurrency(itemStats.totalRevenue), color: 'text-emerald-500' },
                      { label: 'Total COGS', value: formatCurrency(itemStats.totalCogs), color: '' },
                      { label: 'Gross Profit', value: formatCurrency(itemStats.totalGross), color: 'text-emerald-500' },
                      { label: 'Net Profit', value: formatCurrency(itemStats.totalNet), color: 'text-emerald-500' },
                      { label: 'Avg Sell Price', value: formatCurrency(itemStats.avgSellingPrice), color: '' },
                    ].map(s => (
                      <div key={s.label} className="px-4 py-3">
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
                        <p className={`text-sm font-bold mt-0.5 ${s.color || (isDark ? 'text-white' : 'text-gray-900')}`}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Profit Margin Bar */}
              {selectedItem && itemStats.totalRevenue > 0 && (
                <div className={card}>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Profitability Breakdown</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Gross Margin', value: itemStats.totalRevenue > 0 ? ((itemStats.totalGross / itemStats.totalRevenue) * 100) : 0, color: 'bg-emerald-500' },
                      { label: 'Net Margin', value: itemStats.totalRevenue > 0 ? ((itemStats.totalNet / itemStats.totalRevenue) * 100) : 0, color: 'bg-teal-500' },
                      { label: 'COGS Ratio', value: itemStats.totalRevenue > 0 ? ((itemStats.totalCogs / itemStats.totalRevenue) * 100) : 0, color: 'bg-amber-400' },
                    ].map(m => (
                      <div key={m.label}>
                        <div className="flex justify-between mb-1">
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{m.label}</span>
                          <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{m.value.toFixed(1)}%</span>
                        </div>
                        <div className={`h-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                          <div className={`h-2 rounded-full ${m.color} transition-all duration-500`} style={{ width: `${Math.min(100, Math.max(0, m.value))}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
                    <div><p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Paid Sales</p><p className="text-lg font-bold text-emerald-500">{itemStats.paid}</p></div>
                    <div><p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Pending Sales</p><p className="text-lg font-bold text-amber-500">{itemStats.pending}</p></div>
                    <div><p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Transactions</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{itemSales.length}</p></div>
                    <div><p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Profit per Unit</p><p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{itemStats.totalQty > 0 ? formatCurrency(itemStats.totalNet / itemStats.totalQty) : '—'}</p></div>
                  </div>
                </div>
              )}

              {/* Sales Table */}
              <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Sales History
                    <span className={`ml-2 text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({itemSales.length} records)</span>
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className={th}>Date</th>
                        <th className={th}>Customer</th>
                        <th className={th}>Qty</th>
                        <th className={th}>Sell Price</th>
                        <th className={th}>Discount</th>
                        <th className={th}>Total</th>
                        <th className={th}>COGS</th>
                        <th className={th}>Gross Profit</th>
                        <th className={th}>Net Profit</th>
                        <th className={th}>Payment</th>
                        <th className={th}>Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {itemSales.map(s => (
                        <tr key={s.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                          <td className={td}>{formatDate(s.date)}</td>
                          <td className={`${td} font-medium`}>{s.customerName}</td>
                          <td className={td}>{s.quantitySold}</td>
                          <td className={td}>{formatCurrency(s.sellingPrice)}</td>
                          <td className={td}>{s.discount > 0 ? <span className="text-red-500">-{formatCurrency(s.discount)}</span> : <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>—</span>}</td>
                          <td className={`${td} font-semibold`}>{formatCurrency(s.totalSaleValue)}</td>
                          <td className={td}>{formatCurrency(s.cogs)}</td>
                          <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(s.grossProfit)}</td>
                          <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(s.netProfit)}</td>
                          <td className={td}><span className={statusBadge(s.paymentStatus)}>{s.paymentStatus}</span></td>
                          <td className={`${td} max-w-xs truncate`}>{s.notes || '—'}</td>
                        </tr>
                      ))}
                      {itemSales.length === 0 && (
                        <tr><td colSpan={11} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No sales match the selected filters</td></tr>
                      )}
                    </tbody>
                    {itemSales.length > 0 && (
                      <tfoot className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                        <tr>
                          <td colSpan={2} className={`px-4 py-3 text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Totals ({itemSales.length})</td>
                          <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{itemStats.totalQty}</td>
                          <td colSpan={2} />
                          <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(itemStats.totalRevenue)}</td>
                          <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(itemStats.totalCogs)}</td>
                          <td className="px-4 py-3 text-sm font-bold text-emerald-500">{formatCurrency(itemStats.totalGross)}</td>
                          <td className="px-4 py-3 text-sm font-bold text-emerald-500">{formatCurrency(itemStats.totalNet)}</td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── VENDOR PAYABLES TAB ── */}
      {activeTab === 'vendors' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Owed', value: formatCurrency(totalOwed), color: isDark ? 'text-white' : 'text-gray-900' },
              { label: 'Total Paid', value: formatCurrency(totalPaid), color: 'text-emerald-500' },
              { label: 'Balance Remaining', value: formatCurrency(totalOwed - totalPaid), color: (totalOwed - totalPaid) > 0 ? 'text-red-500' : 'text-emerald-500' },
              { label: 'Vendors', value: String(vendorBreakdown.length), color: isDark ? 'text-white' : 'text-gray-900' },
            ].map(k => (
              <div key={k.label} className={`${card} !p-4`}>
                <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{k.label}</p>
                <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
              </div>
            ))}
          </div>

          <div className={card}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Monthly Vendor Payments (Last 6 Months)</h3>
              <button onClick={handleExportVendorPDF} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">
                <i className="ri-file-pdf-line" /> Export PDF
              </button>
            </div>
            {vendorPayments.length === 0 ? (
              <div className={`flex flex-col items-center justify-center py-12 ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>
                <i className="ri-bar-chart-2-line text-4xl mb-2" />
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No vendor payments recorded yet</p>
              </div>
            ) : (
              <div className="flex items-end gap-3 h-48">
                {paymentTrend.map(m => {
                  const heightPct = maxTrendAmount > 0 ? (m.amount / maxTrendAmount) * 100 : 0;
                  return (
                    <div key={m.key} className="flex-1 flex flex-col items-center gap-1 group">
                      <div className="relative w-full flex flex-col items-center justify-end" style={{ height: '160px' }}>
                        {m.amount > 0 && (
                          <div className={`absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-800 text-white'}`}>
                            {formatCurrency(m.amount)}
                          </div>
                        )}
                        <div className="w-full rounded-t-md bg-emerald-500 transition-all duration-500 min-h-[4px]" style={{ height: `${Math.max(4, heightPct)}%` }} />
                      </div>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{m.label}</span>
                      {m.amount > 0 && <span className="text-xs font-medium text-emerald-500">{formatCurrency(m.amount)}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
            <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Vendor Breakdown</h3>
              <button onClick={handleExportVendorCSV} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}><i className="ri-download-line" /> Export CSV</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={th}>Vendor</th>
                    <th className={th}>Total Purchased</th>
                    <th className={th}>Total Paid</th>
                    <th className={th}>Balance Owed</th>
                    <th className={th}>% Paid</th>
                    <th className={th}>Payments</th>
                    <th className={th}>Last Payment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {vendorBreakdown.map(v => {
                    const balance = v.totalPurchased - v.totalPaid;
                    const pct = v.totalPurchased > 0 ? Math.min(100, Math.round((v.totalPaid / v.totalPurchased) * 100)) : 100;
                    return (
                      <tr key={v.vendorName} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                        <td className={`${td} font-medium`}>{v.vendorName}</td>
                        <td className={td}>{formatCurrency(v.totalPurchased)}</td>
                        <td className={`${td} text-emerald-500 font-semibold`}>{formatCurrency(v.totalPaid)}</td>
                        <td className={`${td} font-bold ${balance > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{formatCurrency(balance)}</td>
                        <td className={td}>
                          <div className="flex items-center gap-2">
                            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs whitespace-nowrap">{pct}%</span>
                          </div>
                        </td>
                        <td className={td}>{v.paymentCount}</td>
                        <td className={td}>{v.lastPayment ? formatDate(v.lastPayment) : '—'}</td>
                      </tr>
                    );
                  })}
                  {vendorBreakdown.length === 0 && (
                    <tr><td colSpan={7} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No vendor data yet. Add inventory items with supplier names.</td></tr>
                  )}
                </tbody>
                {vendorBreakdown.length > 0 && (
                  <tfoot className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                    <tr>
                      <td className={`px-4 py-3 text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Totals</td>
                      <td className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(totalOwed)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-emerald-500">{formatCurrency(totalPaid)}</td>
                      <td className={`px-4 py-3 text-sm font-bold ${(totalOwed - totalPaid) > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{formatCurrency(totalOwed - totalPaid)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
            <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>All Vendor Payment Transactions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={th}>Date</th>
                    <th className={th}>Vendor</th>
                    <th className={th}>Related Item</th>
                    <th className={th}>Amount</th>
                    <th className={th}>Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {[...vendorPayments].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(p => (
                    <tr key={p.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                      <td className={td}>{formatDate(p.date)}</td>
                      <td className={`${td} font-medium`}>{p.vendorName}</td>
                      <td className={td}>{p.itemName ? <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{p.itemName}</span> : <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>—</span>}</td>
                      <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(p.amount)}</td>
                      <td className={`${td} max-w-xs truncate`}>{p.referenceNote || '—'}</td>
                    </tr>
                  ))}
                  {vendorPayments.length === 0 && (
                    <tr><td colSpan={5} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No payments recorded yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
